import React, { useEffect, useState } from 'react';
import { BubbleButton, BubbleCheckbox, BubbleFormGroup, BubbleSelect } from '../BubbleUI';
import { Brand, BrandDateFormat, HourFormat } from '@go-shared/types/brand';
import { updateBrand } from '@go-client/lib/api/brand';
import { createPortal } from 'react-dom';
import { DEFAULT_BRAND_DATE_FORMAT, DEFAULT_HOUR_FORMAT, formatDateUTC } from '@go-shared/lib/time';
import { useServerInfo } from '@go-client/context/ServerInfoContext';

interface BrandDateTimeFormDataBase {
  hourFormat: HourFormat;
  dateFormat: BrandDateFormat;
}

type BrandDateTimeFormData = BrandDateTimeFormDataBase & {
  _dirty: boolean;
  _errors: Partial<BrandFormError>;
};

type BrandFormError = { [K in keyof BrandDateTimeFormDataBase]: string };

function DEFAULT_FORM_DATA(brand?: Brand): BrandDateTimeFormData {
  return {
    hourFormat: brand?.hourFormat ?? DEFAULT_HOUR_FORMAT,
    dateFormat: brand?.dateFormat ?? DEFAULT_BRAND_DATE_FORMAT,
    _dirty: false,
    _errors: {},
  };
}

interface BrandFormProps {
  edit: Brand;
  onClose?: () => void;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

export default function BrandDateTimeForm(props: BrandFormProps) {
  const { languages, defaultLanguage } = useServerInfo();

  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<BrandDateTimeFormData>(DEFAULT_FORM_DATA(props.edit));

  const dayFormatOptions: Array<{ value: BrandDateFormat['day']; label: string }> = [
    {
      value: 'numeric',
      label: 'Numeric',
    },
    {
      value: '2-digit',
      label: 'Two-digits',
    },
  ];
  const [dayValue, setDayValue] = useState(
    dayFormatOptions.filter(opt => opt.value === props?.edit?.dateFormat.day).pop()
  );

  const weekdayFormatOptions: Array<{ value: BrandDateFormat['weekday']; label: string }> = [
    {
      value: 'long',
      label: 'Long',
    },
    {
      value: 'short',
      label: 'Short',
    },
  ];
  const [weekdayValue, setWeekdayValue] = useState(
    weekdayFormatOptions.filter(opt => opt.value === props?.edit?.dateFormat.weekday).pop()
  );

  const monthFormatOptions: Array<{ value: BrandDateFormat['month']; label: string }> = [
    {
      value: 'long',
      label: 'Long',
    },
    {
      value: 'short',
      label: 'Short',
    },
  ];
  const [monthValue, setMonthValue] = useState(
    monthFormatOptions.filter(opt => opt.value === props?.edit?.dateFormat.month).pop()
  );

  const yearFormatOptions: Array<{ value: BrandDateFormat['year']; label: string }> = [
    {
      value: 'numeric',
      label: 'Numeric',
    },
    {
      value: '2-digit',
      label: 'Two-digits',
    },
  ];
  const [yearValue, setYearValue] = useState(
    yearFormatOptions.filter(opt => opt.value === props?.edit?.dateFormat.year).pop()
  );

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof BrandDateTimeFormDataBase>(
    key: T,
    val: BrandDateTimeFormDataBase[T]
  ) {
    setFormData({ ...formData, [key]: val, _dirty: true });
  }

  async function submitForm() {
    setSaving(true);
    try {
      if (props.edit) {
        // user intends to edit an existing property
        await updateBrand(props.edit._id, {
          ...props.edit,

          hourFormat: formData.hourFormat,
          dateFormat: formData.dateFormat,
        });
      }

      await props.onFormSubmitted();
      if (props.onClose) props.onClose();
    } finally {
      setSaving(false);
    }
  }

  const errored = Object.keys(formData._errors).length > 0;

  const formHtml = (
    <div className="grid grid-cols-4 gap-8">
      <BubbleFormGroup
        label="Hour Format"
        className="col-span-4"
        error={formData._errors.hourFormat}
        asterisk
      >
        <div className="flex items-center">
          <BubbleCheckbox
            className="mr-3"
            label="12"
            checked={formData.hourFormat === '12'}
            onValue={chk => {
              if (chk) {
                changeForm('hourFormat', '12');
              }
            }}
          />
          <BubbleCheckbox
            label="24"
            checked={formData.hourFormat === '24'}
            onValue={chk => {
              if (chk) {
                changeForm('hourFormat', '24');
              }
            }}
          />
        </div>
      </BubbleFormGroup>

      <BubbleFormGroup
        label="Date Format"
        className="col-span-4"
        error={formData._errors.dateFormat}
        asterisk
      >
        <div className="flex items-center gap-4">
          <BubbleFormGroup label="Weekday" className="flex-1">
            <div className="flex items-start">
              <BubbleSelect
                block
                allowUndefined={false}
                value={weekdayValue}
                options={weekdayFormatOptions}
                labelExtractor={opt => opt.label}
                onValue={selected => {
                  setWeekdayValue(selected);
                  changeForm('dateFormat', { ...formData.dateFormat, weekday: selected?.value });
                }}
              />
            </div>
          </BubbleFormGroup>

          <BubbleFormGroup label="Day" className="flex-1">
            <BubbleSelect
              block
              allowUndefined={false}
              value={dayValue}
              options={dayFormatOptions}
              labelExtractor={opt => opt.label}
              onValue={selected => {
                setDayValue(selected);
                changeForm('dateFormat', { ...formData.dateFormat, day: selected?.value });
              }}
            />
          </BubbleFormGroup>

          <BubbleFormGroup label="Month" className="flex-1">
            <div className="flex items-start">
              <BubbleSelect
                block
                allowUndefined={false}
                value={monthValue}
                options={monthFormatOptions}
                labelExtractor={opt => opt.label}
                onValue={selected => {
                  setMonthValue(selected);
                  changeForm('dateFormat', { ...formData.dateFormat, month: selected?.value });
                }}
              />
            </div>
          </BubbleFormGroup>

          <BubbleFormGroup label="Year" className="flex-1">
            <div className="flex items-start">
              <BubbleSelect
                block
                allowUndefined={false}
                value={yearValue}
                options={yearFormatOptions}
                labelExtractor={opt => opt.label}
                onValue={selected => {
                  setYearValue(selected);
                  changeForm('dateFormat', { ...formData.dateFormat, year: selected?.value });
                }}
              />
            </div>
          </BubbleFormGroup>
        </div>

        <div className="flex items-center mt-4">
          <BubbleCheckbox
            inputClassName="bg-white"
            checked={formData.dateFormat.useOrdinals === true}
            onValue={chk => {
              changeForm('dateFormat', { ...formData.dateFormat, useOrdinals: chk });
            }}
            label="Use ordinals"
            subtitle="Use ordinals for English language allowing to use st, nd, rd or th on the day value."
          />
        </div>

        <div className="flex flex-col gap-4">
          <div className="mt-4">EXAMPLES:</div>

          <div>
            <ul className="mt grid grid-cols-2 gap-4">
              <li className=" ">
                <b>{defaultLanguage.englishLabel}</b>
                <span className="ml-4">
                  {formatDateUTC(new Date(), defaultLanguage.key, formData.dateFormat)}
                </span>
              </li>

              {languages.map(language => (
                <li>
                  <b>{language.englishLabel}</b>
                  <span className="ml-4">
                    {formatDateUTC(new Date(), language.key, formData.dateFormat)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </BubbleFormGroup>
    </div>
  );

  const formActions = (
    <div className="flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || errored || saving}
        icon="undo"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || errored || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
